import { BaseQueryFn, createApi, FetchArgs, fetchBaseQuery, FetchBaseQueryError } from '@reduxjs/toolkit/query/react'
import Auth from '../utils/auth'
import { generateRandomNumber, retrieveErrorMessage } from '../utils/helpers'
import {
  DateRequestResponse,
  User,
  DateScheduleResponse,
  SupportMessage,
  SupportConversation,
  FavouriteActivity,
  Interest,
  DateRequest,
  FriendCategory,
  FriendRequest,
  Friend,
  PaginationInfo,
  MessageType,
  DateTimeslot,
  DateSchedule,
  DAYS_OF_THE_WEEK,
  UserDetails,
  NotificationSettingType,
  OpenDateRequest,
  OpenDateRequestsFormValues,
  SupplySuggestion,
  Supply,
  TBuckBreaker,
  TBuckBreakerExpense
} from '../utils/types'
import { getEnv, getFeedback } from '../platform'
import { selectCurrentUser, updateNotificationSettings } from '../stores/authStore'
import { getSharedStore } from '../storeRef'
import {
  LoginResponse,
  LoginArgs,
  RegisterArgs,
  PreferredDatingActivity,
  SupportMessageResponse,
  SupportMessageRequest,
  GetConversationResponse,
  GetConversationRequest,
  AddParticipantResponse,
  AddParticipantRequest,
  DateScoreboardResponse,
  DateCommentsResponse,
  DateExpenseResponse,
  ToggleRecurringResponse,
  ToggleRecurringRequest,
  AddDateScoreboardRequest,
  AddDateCommentRequest,
  AddDateMediaRequest,
  DateMemoryScoreboard,
  ProfileResponse,
  AvailabilityResponse,
  BusyTimesResponse,
  FetchScoreboardResponse,
  FetchScoreboardRequest,
  DateRequestsRequest,
  RescheduleDateRequest,
  NotificationResponse,
  ChatMessageRequest,
  ResetPasswordRequest,
  DeleteParticipantRequest,
  AddCommentResponse,
  DateMediaResponse,
  DateExpenseUpdateOrDeleteRequest,
  DateExpenseRequest,
  AddDateMediaResponse,
  ParticipantRequestResponse,
  ParticipantRequestRequest,
  ParticipantResponse,
  FetchBuckBreakersRequest,
  FetchBuckBreakersResponse,
  FetchScoreboardsRequest,
  FetchScoreboardsResponse,
  CreateOpenScoreboardRequest,
  CreateOpenScoreboardResponse,
  FetchScoreboardDetailRequest,
  FetchScoreboardDetailResponse,
  FetchScoreboardTableRequest,
  FetchScoreboardTableResponse,
  AddScoreboardParticipantRequest,
  AddScoreboardParticipantResponse,
  FetchScoreboardResultsResponse,
  FetchScoreboardResultRequest,
  FetchScoreboardResultResponse,
  CreateScoreboardResultRequest,
  CreateScoreboardResultResponse,
  UpdateScoreboardResultResponse,
  UpdateScoreboardResultRequest,
  UpsertPushTokenRequest,
  UpsertPushTokenResponse,
  DeletePushTokenRequest,
  DeletePushTokenResponse,
  RealtimeConfig,
  FetchRealtimeConfigResponse,
  FetchTimelineRequest,
  FetchTimelineResponse,
} from './apiTypes'
export * from './apiTypes'

export let baseUrl = 'http://localhost:8000/api/v1'

export function getApiBaseUrl(): string {
  return getEnv().apiBaseUrl
}

export function initSharedApiConfig() {
  baseUrl = getEnv().apiBaseUrl
}

const dynamicBaseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extra
) => {
  const raw = fetchBaseQuery({
    baseUrl: getEnv().apiBaseUrl,
    prepareHeaders: (headers: Headers) => {
      const token = Auth.fetchToken()
      if (token) headers.set('Authorization', `Bearer ${token}`)
      const { clientPlatform } = getEnv()
      if (clientPlatform) headers.set('X-Client-Platform', clientPlatform)
      return headers
    },
  })
  return raw(args, api, extra)
}

export const gusiberiApi = createApi({
  reducerPath: 'gusiberiApi',
  baseQuery: dynamicBaseQuery,
  tagTypes: [
    'approved_requests',
    'pending_requests',
    'pending_friend_requests',
    'all_friends',
    'user_profile',
    'fetch_friendships',
    'friend_categories',
    'date_expense_payments',
    'fetch_date_media',
    'active_pending_requests',
    'fetch_date_request',
    'fetch_friends_open_date_request',
    'my_open_date_requests',
    'support_issues',
    'support_conversation',
    'current_user',
    'favourite_activities',
  ],
  endpoints: (builder) => ({
    login: builder.mutation<LoginResponse, LoginArgs>({
      query: (body) => ({ url: 'login', method: 'POST', body }),
    }),
    register: builder.mutation<LoginResponse, RegisterArgs>({
      query: (body) => ({ url: 'register', method: 'POST', body })
    }),
    googleAuth: builder.mutation<LoginResponse, {id_token: string, params?: any}>({
      query: body => ({ url: 'google_auth', method: 'POST', body })
    }),
    currentUser: builder.query<{payload: UserDetails}, {}>({
      query: () => 'user_details',
      providesTags: ['current_user'],
    }),
    setUserProfileInfo: builder.mutation<{message: string}, Partial<User>>({
      query: (body) => ({ url: 'user_profile', method: 'POST', body }),
      invalidatesTags: ['current_user'],
    }),
    preferredDatingActivity: builder.query<{payload: PreferredDatingActivity[]}, {}>({
      query: () => 'favourite_activities',
      providesTags: ['favourite_activities'],
    }),
    dateSchedule: builder.query<DateScheduleResponse, {}>({
      query: () => 'date_schedules',
      transformResponse: (response: {payload: DateSchedule[]}): DateScheduleResponse => {
        const data = response.payload.reduce((prev, date) => {
          const day = DAYS_OF_THE_WEEK[date.day_of_the_week - 1]
          if (!prev[day]) {
            prev[day] = date
          }
          return prev
        }, {} as DateScheduleResponse)
        return data
      }
    }),
    fetchUpcomingDates: builder.query<DateRequestResponse, {page?: number} | void>({
      query: (arg) => {
        const page =
          arg && typeof arg === 'object' && arg.page != null ? arg.page : 1
        return `date_requests/approved?page=${page}`
      },
      serializeQueryArgs: ({ endpointName }) => endpointName,
      merge: (currentCache, incoming, { arg }) => {
        const page =
          arg && typeof arg === 'object' && arg.page != null ? arg.page : 1
        if (page <= 1 || !currentCache.payload?.length) {
          currentCache.payload = incoming.payload
          currentCache.pagination_info = incoming.pagination_info
          return
        }
        const existingIds = new Set(currentCache.payload.map((d) => d.request_id))
        currentCache.payload.push(
          ...incoming.payload.filter((d) => !existingIds.has(d.request_id))
        )
        currentCache.pagination_info = incoming.pagination_info
      },
      forceRefetch: ({ currentArg, previousArg }) => {
        const currentPage =
          currentArg && typeof currentArg === 'object' && currentArg.page != null
            ? currentArg.page
            : 1
        const previousPage =
          previousArg && typeof previousArg === 'object' && previousArg.page != null
            ? previousArg.page
            : 1
        return currentPage !== previousPage
      },
      providesTags: ['approved_requests']
    }),
    fetchPastDates: builder.query<DateRequestResponse, {page?: number} | void>({
      query: (arg) => {
        const page =
          arg && typeof arg === 'object' && arg.page != null ? arg.page : 1
        return `date_requests/approved?type=past&page=${page}`
      },
      serializeQueryArgs: ({ endpointName }) => endpointName,
      merge: (currentCache, incoming, { arg }) => {
        const page =
          arg && typeof arg === 'object' && arg.page != null ? arg.page : 1
        if (page <= 1 || !currentCache.payload?.length) {
          currentCache.payload = incoming.payload
          currentCache.pagination_info = incoming.pagination_info
          return
        }
        const existingIds = new Set(currentCache.payload.map((d) => d.request_id))
        currentCache.payload.push(
          ...incoming.payload.filter((d) => !existingIds.has(d.request_id))
        )
        currentCache.pagination_info = incoming.pagination_info
      },
      forceRefetch: ({ currentArg, previousArg }) => {
        const currentPage =
          currentArg && typeof currentArg === 'object' && currentArg.page != null
            ? currentArg.page
            : 1
        const previousPage =
          previousArg && typeof previousArg === 'object' && previousArg.page != null
            ? previousArg.page
            : 1
        return currentPage !== previousPage
      },
      providesTags: ['approved_requests']
    }),
    deactivateDay: builder.mutation<{message: string}, {day_of_week: number}>({
      query: (body) => ({ url: 'date_schedules/deactivate', method: 'POST', body })
    }),
    activateDay: builder.mutation<{message: string}, {day_of_week: number}>({
      query: (body) => ({ url: 'date_schedules/activate', method: 'POST', body })
    }),
    addSchedule: builder.mutation<{payload: DateTimeslot}, Omit<DateTimeslot, "id" | "day_of_the_week">>({
      query: (body) => ({ url: 'date_schedules', method: 'POST', body })
    }),
    deleteSchedule: builder.mutation<{message: string}, {id: number}>({
      query: (body) => ({ url: `date_schedules/${body.id}`, method: 'DELETE' })
    }),
    changePassword: builder.mutation<{message: string}, {old_password: string, new_password: string}>({
      query: (body) => ({ url: 'change_password', method: 'POST', body })
    }),
    deleteAccount: builder.mutation<{message: string}, {password: string}>({
      query: (body) => ({ url: 'delete_account', method: 'POST', body })
    }),
    sendSupportMessage: builder.mutation<SupportMessageResponse, SupportMessageRequest>({
      query: (body) => ({ url: 'supports', method: 'POST', body }),
      invalidatesTags: ['support_issues'],
    }),
    fetchSupportIssues: builder.query<{payload: SupportMessage[]}, {}>({
      query: () => 'supports',
      providesTags: ['support_issues'],
    }),
    fetchSupportConversation: builder.query<GetConversationResponse, GetConversationRequest>({
      query: params => ({ url: `supports/${params.id}/messages`}),
      providesTags: (_result, _error, arg) => [{ type: 'support_conversation', id: arg.id }],
    }),
    postSupportConversation: builder.mutation<{payload: SupportConversation}, {message: string, id: number}>({
      query: (body) => ({ url: `supports/${body.id}/messages`, method: 'POST', body }),
      invalidatesTags: (_result, _error, arg) => [
        'support_issues',
        { type: 'support_conversation', id: arg.id },
      ],
    }),
    fetchFavouriteActivities: builder.query<{payload: FavouriteActivity[]}, {}>({
      query: () => 'favourite_activities',
      providesTags: ['favourite_activities'],
    }),
    postFavouriteActivities: builder.mutation<{payload: FavouriteActivity}, Partial<FavouriteActivity>>({
      query: body => ({ url: 'favourite_activities', method: 'POST', body }),
      invalidatesTags: ['favourite_activities'],
    }),
    deleteFavouriteActivity: builder.mutation<{message: string}, {id: number}>({
      query: body => ({ url: `favourite_activities/${body.id}`, method: 'DELETE', body }),
      invalidatesTags: ['favourite_activities'],
    }),
    updateFavouriteActivities: builder.mutation<{payload: FavouriteActivity}, FavouriteActivity>({
      query: body => ({ url: `favourite_activities/${body.id}`, method: 'PUT', body }),
      invalidatesTags: ['favourite_activities'],
    }),
    fetchInterests: builder.query<{payload: Interest[]}, void>({
      query: () => 'favourite_activities/interests',
      keepUnusedDataFor: 60 * 60,
    }),
    searchUsers: builder.query<{payload: User[]}, {query: string}>({
      query: params => ({ url: `search?q=${params.query}` })
    }),
    profile: builder.query<ProfileResponse, {username: string}>({
      query: params => ({ url: `users/${params.username}/profile` }),
      providesTags: ['user_profile']
    }),
    availability: builder.query<AvailabilityResponse, {username: string}>({
      query: params => ({ url: `users/${params.username}/availability` })
    }),
    busyTimes: builder.query<BusyTimesResponse, {username: string}>({
      query: params => ({ url: `users/${params.username}/busy_times` })
    }),
    sendDateRequest: builder.mutation<{payload: DateRequest}, Omit<DateRequestsRequest, 'request_id'>>({
      query: body => ({ url: 'date_requests', method: 'POST', body }),
      invalidatesTags: ["approved_requests"]
    }),
    scheduleAnother: builder.mutation<{payload: DateRequest}, {request_id: string, start_date: string, end_date: string}>({
      query: body => ({ url: `date_requests/${body.request_id}/schedule_another`, method: 'POST', body }),
      invalidatesTags: ["approved_requests"]
    }),
    updateSentDateRequest: builder.mutation<{message: string}, DateRequestsRequest>({
      query: body => ({ url: `date_requests/${body.request_id}`, method: 'PUT', body})
    }),
    fetchDateRequest: builder.query<{payload: DateRequest}, { request_id: string }>({
      query: (params) => `date_requests/${params.request_id}`,
      providesTags: ["fetch_date_request"]
    }),
    fetchActivePendingRequest: builder.query<DateRequestResponse, {page?: number} | void>({
      query: (arg) => {
        const page =
          arg && typeof arg === 'object' && arg.page != null ? arg.page : 1
        return `date_requests?type=relevant_pending&page=${page}`
      },
      serializeQueryArgs: ({ endpointName }) => endpointName,
      merge: (currentCache, incoming, { arg }) => {
        const page =
          arg && typeof arg === 'object' && arg.page != null ? arg.page : 1
        if (page <= 1 || !currentCache.payload?.length) {
          currentCache.payload = incoming.payload
          currentCache.pagination_info = incoming.pagination_info
          return
        }
        const existingIds = new Set(currentCache.payload.map((d) => d.request_id))
        currentCache.payload.push(
          ...incoming.payload.filter((d) => !existingIds.has(d.request_id))
        )
        currentCache.pagination_info = incoming.pagination_info
      },
      forceRefetch: ({ currentArg, previousArg }) => {
        const currentPage =
          currentArg && typeof currentArg === 'object' && currentArg.page != null
            ? currentArg.page
            : 1
        const previousPage =
          previousArg && typeof previousArg === 'object' && previousArg.page != null
            ? previousArg.page
            : 1
        return currentPage !== previousPage
      },
      providesTags: ["active_pending_requests"]
    }),
    fetchPastPendingRequest: builder.query<DateRequestResponse, {page?: number} | void>({
      query: (arg) => {
        const page =
          arg && typeof arg === 'object' && arg.page != null ? arg.page : 1
        return `date_requests?type=past_pending&page=${page}`
      },
      serializeQueryArgs: ({ endpointName }) => endpointName,
      merge: (currentCache, incoming, { arg }) => {
        const page =
          arg && typeof arg === 'object' && arg.page != null ? arg.page : 1
        if (page <= 1 || !currentCache.payload?.length) {
          currentCache.payload = incoming.payload
          currentCache.pagination_info = incoming.pagination_info
          return
        }
        const existingIds = new Set(currentCache.payload.map((d) => d.request_id))
        currentCache.payload.push(
          ...incoming.payload.filter((d) => !existingIds.has(d.request_id))
        )
        currentCache.pagination_info = incoming.pagination_info
      },
      forceRefetch: ({ currentArg, previousArg }) => {
        const currentPage =
          currentArg && typeof currentArg === 'object' && currentArg.page != null
            ? currentArg.page
            : 1
        const previousPage =
          previousArg && typeof previousArg === 'object' && previousArg.page != null
            ? previousArg.page
            : 1
        return currentPage !== previousPage
      },
    }),
    fetchSentRequests: builder.query<{payload: DateRequest[]}, {}>({
      query: () => ({ url: `date_requests?type=request_sent` }),
      providesTags: ['pending_requests']
    }),
    fetchReceivedRequests: builder.query<{payload: DateRequest[]}, {}>({
      query: () => ({ url: `date_requests?type=request_recieved` }),
      providesTags: ['pending_requests']
    }), 
    acceptDateRequest: builder.mutation<{message: string}, {id: string, message?: string}>({
      query: body => ({ url: `date_requests/${body.id}/accept`, method: 'POST' }),
      invalidatesTags: ['pending_requests', 'approved_requests', 'active_pending_requests']
    }),
    declineDateRequest: builder.mutation<{message: string}, {id: string, message?: string}>({
      query: body => ({ url: `date_requests/${body.id}/decline`, method: 'POST', body }),
      invalidatesTags: ['pending_requests', 'active_pending_requests']
    }),
    rescheduleDateRequest: builder.mutation<{message: string}, RescheduleDateRequest>({
      query: body => ({ url: `date_requests/${body.id}/reschedule`, method: 'POST', body }),
      invalidatesTags: ['approved_requests'],
    }),
    toggleRecurring: builder.mutation<ToggleRecurringResponse, ToggleRecurringRequest>({
      query: body => ({ url: `date_requests/${body.id}/toggle_recurring`, method: 'POST' }),
      onQueryStarted: async ({id}, { dispatch, queryFulfilled }) => {
        const patchResult = dispatch(
          gusiberiApi.util.updateQueryData("fetchDateRequest", {request_id: id}, dateRequest => {
            dateRequest.payload.is_recurring = !dateRequest.payload.is_recurring
          })
        )
        
        queryFulfilled.then(() => {
          getFeedback().showToast("Recurring status updated!", "success")
        }).catch(e => {
          getFeedback().showToast(retrieveErrorMessage(e), "error")
          patchResult.undo()
        })
      }
    }),
    addReviewRequest: builder.mutation<{payload: DateRequest}, {review: string, id: string}>({
      query: body => ({ url: `date_requests/${body.id}/review`, method: 'POST', body })
    }),
    sendFriendRequest: builder.mutation<{message: string}, {sent_to: string}>({
      query: body => ({ url: 'friend_requests', method: 'POST', body }),
      invalidatesTags: ['user_profile', 'fetch_friendships', 'pending_friend_requests']
    }),
    deleteFriendGroupRequest: builder.mutation<{message: string}, {id: number}>({
      query: body => ({ url: `friend_categories/${body.id}`, method: 'DELETE', body }),
      invalidatesTags: ['friend_categories', 'fetch_friendships']
    }),
    createFriendGroupRequest: builder.mutation<{payload: FriendCategory}, {name: string}>({
      query: body => ({ url: 'friend_categories', method: 'POST', body }),
      invalidatesTags: ['friend_categories']
    }),
    fetchFriendGroupsRequest: builder.query<{payload: FriendCategory[]}, {}>({
      query: () => 'friend_categories',
      providesTags: ['friend_categories']
    }),
    renameFriendGroupRequest: builder.mutation<{message: string}, {id: number, name: string}>({
      query: body => ({ url: `friend_categories/${body.id}`, method: 'PUT', body }),
      invalidatesTags: ['friend_categories']
    }),
    fetchPendingFriendRequests: builder.query<{payload: FriendRequest[]}, {}>({
      query: () => 'friend_requests',
      providesTags: ['pending_friend_requests']
    }),
    fetchDeclinedFriendRequests: builder.query<{payload: FriendRequest[]}, {}>({
      query: () => 'friend_requests?request_type=declined',
    }),
    getFriendsRequest: builder.query<{payload: Friend[]}, {}>({
      query: () => 'friendships',
      providesTags: ['fetch_friendships']
    }),
    acceptFriendRequest: builder.mutation<{message: string}, {id: number}>({
      query: body => ({ url: `friend_requests/${body.id}/accept`, method: 'POST' }),
      invalidatesTags: ['pending_friend_requests', 'fetch_friendships', 'user_profile']
    }),
    removeFriendRequest: builder.mutation<{message: string}, {friend_username: string}>({
      query: body => ({ url: `friendships/unfriend`, method: 'POST', body }),
      invalidatesTags: ['user_profile', 'fetch_friendships', 'friend_categories']
    }),
    declineFriendRequest: builder.mutation<{message: string}, {id: number}>({
      query: body => ({ url: `friend_requests/${body.id}/decline`, method: 'POST' }),
      invalidatesTags: ['pending_friend_requests']
    }),
    assignFriendsToGroupRequest: builder.mutation<{message: string}, {friend_username: string | string[], category_id: number}>({
      query: body => ({ url: 'friendships/assign_to_category', method: 'POST', body }),
      invalidatesTags: ['friend_categories', 'fetch_friendships']
    }),
    removeFriendsFromGroupRequest: builder.mutation<{message: string}, {friend_username: string | string[], category_id: number}>({
      query: body => ({ url: 'friendships/remove_from_category', method: 'POST', body }),
      invalidatesTags: ['friend_categories', 'fetch_friendships']
    }),
    fetchNotifications: builder.query<NotificationResponse, { page?: number } | void>({
      query: (arg) => {
        const page =
          arg && typeof arg === 'object' && arg.page != null ? arg.page : 1
        return `notifications?page=${page}`
      },
      serializeQueryArgs: ({ endpointName }) => endpointName,
      merge: (currentCache, incoming, { arg }) => {
        const page =
          arg && typeof arg === 'object' && arg.page != null ? arg.page : 1
        if (page <= 1 || !currentCache.payload?.length) {
          currentCache.payload = incoming.payload
          currentCache.pagination_info = incoming.pagination_info
          return
        }
        const existingIds = new Set(currentCache.payload.map((n) => n.id))
        currentCache.payload.push(
          ...incoming.payload.filter((n) => !existingIds.has(n.id))
        )
        currentCache.pagination_info = incoming.pagination_info
      },
      forceRefetch: ({ currentArg, previousArg }) => {
        const currentPage =
          currentArg && typeof currentArg === 'object' && currentArg.page != null
            ? currentArg.page
            : 1
        const previousPage =
          previousArg && typeof previousArg === 'object' && previousArg.page != null
            ? previousArg.page
            : 1
        return currentPage !== previousPage
      },
    }),
    markNotificationAsSeen: builder.mutation<{message: string}, {id: number}>({
      query: body => ({ url: `notifications/${body.id}/mark_as_seen`, method: 'POST' })
    }),
    markAllNotificationAsSeen: builder.mutation<{message: string}, {}>({
      query: () => ({ url: 'notifications/mark_all_as_seen', method: 'POST' }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        const patchResult = dispatch(
          gusiberiApi.util.updateQueryData("fetchNotifications", {}, notifications => {
            notifications.payload = notifications.payload.map(n => ({...n, seen: true}))
          })
        )
        queryFulfilled
          .then(() => {
            getFeedback().showToast("All notifications marked as seen", 'success')
          })
          .catch(e => {
            getFeedback().showToast(retrieveErrorMessage(e), 'error')
            patchResult.undo()
        })
      }
    }),
    fetchTimeline: builder.query<FetchTimelineResponse, FetchTimelineRequest | void>({
      query: (arg) => `user_activities/timeline?page=${arg?.page ?? 1}`,
      serializeQueryArgs: ({ endpointName }) => endpointName,
      merge: (currentCache, incoming, { arg }) => {
        const page = arg?.page ?? 1
        if (page <= 1 || !currentCache.payload?.length) {
          currentCache.payload = incoming.payload
          currentCache.pagination_info = incoming.pagination_info
          return
        }
        const existingIds = new Set(currentCache.payload.map((activity) => activity.id))
        currentCache.payload.push(
          ...incoming.payload.filter((activity) => !existingIds.has(activity.id))
        )
        currentCache.pagination_info = incoming.pagination_info
      },
      forceRefetch: ({ currentArg, previousArg }) => (currentArg?.page ?? 1) !== (previousArg?.page ?? 1),
    }),
    sendChatMessage: builder.mutation<{payload: MessageType}, ChatMessageRequest>({
      query: body => ({ url: `date_requests/${body.request_id}/date_discussions`, method: 'POST', body })
    }),
    fetchChatMessages: builder.query<{payload: MessageType[], pagination_info: PaginationInfo}, {request_id: string, page?: number, offset?: string}>({
      query: body => ({ url: `date_requests/${body.request_id}/date_discussions?page=${body.page || 1}&offset=${body.offset || ''}` })
    }),
    markMessagesAsSeen: builder.mutation<{message: string}, {request_id: string}>({
      query: body => ({ url: `date_requests/${body.request_id}/date_discussions/mark_as_seen`, method: 'POST', body })
    }),
    resendActivation: builder.mutation<{message: string}, {}>({
      query: body => ({ url: 'users/send_activation_link', method: 'POST', body })
    }),
    activateUser: builder.mutation<{message: string}, {activation_code: string}>({
      query: body => ({ url: 'activate', body, method: 'POST' })
    }),
    resetPasswordRequest: builder.mutation<{message: string}, {email: string}>({
      query: body => ({ url: 'password_reset_request', body, method: 'POST' })
    }),
    resetPassword: builder.mutation<{message: string}, ResetPasswordRequest>({
      query: body => ({ url: 'password_reset', body, method: 'POST' })
    }),
    addParticipant: builder.mutation<AddParticipantResponse, AddParticipantRequest>({
      query: body => ({ url: `date_requests/${body.request_id}/date_participants`, body, method: 'POST' }),
      onQueryStarted: async ({request_id }, { dispatch, queryFulfilled}) => {
        try {
          const response = await queryFulfilled
          dispatch(
            gusiberiApi.util.updateQueryData("fetchSentRequests", {}, activities => {
              activities.payload = activities.payload.map(activity => {
                if (activity.request_id === request_id) {
                  return {...activity, date_participants: [...(activity.date_participants || []), ...response.data.payload]}
                }
                return activity
              })
            })
          )

          dispatch(
            gusiberiApi.util.updateQueryData("fetchDateRequest", { request_id }, dateRequest => {
              dateRequest.payload = {...dateRequest.payload, date_participants: [...(dateRequest.payload.date_participants || []), ...response.data.payload]}
            })
          )
        } catch (e) {
          getFeedback().showToast(retrieveErrorMessage(e), 'error')
        }
      }
    }),
    resendParticipantInvite: builder.mutation<{message: string}, {request_id: string, pid: number}>({
      query: body => ({url: `date_requests/${body.request_id}/date_participants/${body.pid}/resend_invite`, method: 'POST' }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        getFeedback().showToast("invite resent!", "success")
        queryFulfilled.catch(e => getFeedback().showToast(retrieveErrorMessage(e), "error"))
      }
    }),
    removeParticipant: builder.mutation<{message: string}, DeleteParticipantRequest>({
      query: body => ({ url: `date_requests/${body.request_id}/date_participants/${body.participant_id}`, method: 'DELETE' }),
      onQueryStarted: async ({request_id, participant_id}, { dispatch, queryFulfilled }) => {
        const patchResult = dispatch(
          gusiberiApi.util.updateQueryData("fetchDateRequest", {request_id}, request => {
            request.payload = {
              ...request.payload,
              date_participants: request.payload.date_participants?.filter(p => p.id !== participant_id)
            }
          })
        )

        queryFulfilled.catch(() => {
          patchResult.undo()
        })
      }
    }),
    fetchDateScoreboardRequest: builder.query<DateScoreboardResponse, {request_id: string}>({
      query: body => ({ url: `date_requests/${body.request_id}/date_memories?kind=scoreboard` })
    }),
    fetchScoreboard: builder.query<FetchScoreboardResponse, FetchScoreboardRequest>({
      query: body => ({ url: `date_requests/${body.id}/scoreboard` })
    }),
    saveDateScoreboardRequest: builder.mutation<{message: string, payload: DateMemoryScoreboard}, AddDateScoreboardRequest>({
      query: body => ({ url: `date_requests/${body.request_id}/date_memories`, body, method: 'POST' })
    }),
    fetchDateComments: builder.query<DateCommentsResponse, {request_id: string}>({
      query: body => ({ url: `date_requests/${body.request_id}/date_memories?kind=comment` })
    }),
    saveDateComments: builder.mutation<AddCommentResponse, AddDateCommentRequest>({
      query: body => ({ url: `date_requests/${body.request_id}/date_memories`, body, method: 'POST' })
    }),
    deleteMemoryRequest: builder.mutation<{message: string}, {request_id: string, memory_id: number}>({
      query: body => ({ url: `date_requests/${body.request_id}/date_memories/${body.memory_id}`, method: 'DELETE'})
    }),
    fetchDateMedia: builder.query<DateMediaResponse, {request_id: string}>({
      query: body => ({ url: `date_requests/${body.request_id}/date_memories?kind=photo` }),
      providesTags: ["fetch_date_media"]
    }),
    saveDateMedia: builder.mutation<AddDateMediaResponse, AddDateMediaRequest>({
      query: body => ({ url: `date_requests/${body.request_id}/date_memories`, body, method: 'POST'})
    }),
    saveDateExpense: builder.mutation<DateExpenseResponse, DateExpenseRequest>({
      query: body => ({ url: `date_requests/${body.request_id}/date_expenses`, body, method: 'POST'}),
      invalidatesTags: ['date_expense_payments'],
      onQueryStarted: async ({request_id, ...patch}, { dispatch, queryFulfilled}) => {
        const fakeId = generateRandomNumber(5)
        const patchResult = dispatch(
          gusiberiApi.util.updateQueryData("fetchDateExpense", {request_id}, expenses => {
            const tempPatch = {...patch, id: fakeId}
            expenses.payload = {
              expenses: [tempPatch, ...expenses.payload.expenses],
              expense_payments: expenses.payload.expense_payments
            }
          })
        )

        try {
          const response = await queryFulfilled
          dispatch(
            gusiberiApi.util.updateQueryData("fetchDateExpense", {request_id}, expenses => {
              expenses.payload = response.data.payload
            })
          )
        } catch (e) {
          getFeedback().showToast(retrieveErrorMessage(e), "error")
          patchResult.undo()
        }
      }
    }),
    updateDateExpense: builder.mutation<DateExpenseResponse, DateExpenseUpdateOrDeleteRequest>({
      query: body => ({ url: `date_requests/${body.request_id}/date_expenses/${body.id}`, body, method: 'PUT'}),
      invalidatesTags: ['date_expense_payments'],
      onQueryStarted: async ({request_id, ...patch}, { dispatch, queryFulfilled}) => {
        const patchResult = dispatch(
          gusiberiApi.util.updateQueryData("fetchDateExpense", {request_id}, expenses => {
            expenses.payload = {
              expenses: expenses.payload.expenses.map(e => e.id === patch.id ? patch : e),
              expense_payments: expenses.payload.expense_payments
            }
          })
        )

        queryFulfilled
          .then(response => {
            dispatch(
              gusiberiApi.util.updateQueryData("fetchDateExpense", {request_id}, expenses => {
                expenses.payload = response.data.payload
              })
            )
          })
          .catch(e => {
            getFeedback().showToast(retrieveErrorMessage(e), "error")
            patchResult.undo()
          })
      }
    }),
    deleteDateExpense: builder.mutation<DateExpenseResponse, {request_id: string, id: number}>({
      query: body => ({ url: `date_requests/${body.request_id}/date_expenses/${body.id}`, method: 'DELETE'}),
      invalidatesTags: ['date_expense_payments'],
      onQueryStarted: async ({id, request_id}, { dispatch, queryFulfilled}) => {
        const patchResult = dispatch(
          gusiberiApi.util.updateQueryData("fetchDateExpense", {request_id}, expenses => {
            expenses.payload = {
              expenses: expenses.payload.expenses.filter(e => e.id !== id),
              expense_payments: expenses.payload.expense_payments
            }
          })
        )
        queryFulfilled
          .then(response => {
            dispatch(
              gusiberiApi.util.updateQueryData("fetchDateExpense", {request_id}, expenses => {
                expenses.payload = response.data.payload
              })
            )
          })
          .catch(e => {
            patchResult.undo()
            getFeedback().showToast(retrieveErrorMessage(e), "error")
          })
      }
    }),
    fetchDateExpense: builder.query<DateExpenseResponse, {request_id: string}>({
      query: body => ({ url: `date_requests/${body.request_id}/date_expenses` })
    }),
    markExpenseAsPaid: builder.mutation<DateExpenseResponse, {request_id: string, receipt?: string, id: number}>({
      query: body => ({url: `date_requests/${body.request_id}/date_expenses/mark_as_paid`, body: {receipt: body.receipt}, method: 'POST'}),
      onQueryStarted: async ({request_id, ...patch}, {dispatch, queryFulfilled}) => {
        const patchResult = dispatch(
          gusiberiApi.util.updateQueryData("fetchDateExpense", {request_id}, expenses => {
            expenses.payload = {
              expenses: expenses.payload.expenses,
              expense_payments: expenses.payload.expense_payments.map(e => e.id === patch.id ? {...e, ...{payment_status: "paid"}} : e)
            }
          })
        )

        queryFulfilled
          .then(response =>{
            dispatch(
              gusiberiApi.util.updateQueryData("fetchDateExpense", {request_id}, expenses => {
                expenses.payload = response.data.payload
              })
            )
          })
          .catch(e => {
            getFeedback().showToast(retrieveErrorMessage(e), "error")
            patchResult.undo()
          })
      }
    }),
    setNotificationSettings: builder.mutation<{message: string}, {field: NotificationSettingType, value: boolean}>({
      query: body => ({url: 'notification_settings/set', body, method: 'POST'}),
      onQueryStarted: async ({field, value}, { dispatch, queryFulfilled }) => {
        dispatch(updateNotificationSettings({[field]: value}))
        queryFulfilled.catch(e => {
          dispatch(updateNotificationSettings({[field]: !value}))
          getFeedback().showToast(retrieveErrorMessage(e), "error")
        })
      }
    }),
    upsertPushToken: builder.mutation<UpsertPushTokenResponse, UpsertPushTokenRequest>({
      query: body => ({ url: 'push_tokens', method: 'POST', body }),
    }),
    deletePushToken: builder.mutation<DeletePushTokenResponse, DeletePushTokenRequest>({
      query: body => ({
        url: `push_tokens?token=${encodeURIComponent(body.token)}`,
        method: 'DELETE',
      }),
    }),
    fetchRealtimeConfig: builder.query<RealtimeConfig, void>({
      query: () => 'realtime/config',
      transformResponse: (response: FetchRealtimeConfigResponse) => response.payload,
    }),
    deleteDateRequest: builder.mutation<{message: string}, {request_id: string}>({
      query: body => ({ url: `date_requests/${body.request_id}`, method: 'DELETE' }),
      invalidatesTags: ['approved_requests']
    }),
    sendParticipantRequest: builder.mutation<ParticipantRequestResponse, ParticipantRequestRequest>({
      query: body => ({ url: `date_requests/${body.request_id}/date_participant_requests`, method: 'POST', body })
    }),
    updateParticipantRequest: builder.mutation<{message: string}, {request_id: string, prequest_id: number, approval_status: ParticipantResponse}>({
      query: body => ({ url: `date_requests/${body.request_id}/date_participant_requests/${body.prequest_id}`, method: 'PUT', body }),
      onQueryStarted: async ({request_id, prequest_id, approval_status}, { dispatch, queryFulfilled }) => {
        const patchResult = dispatch(
          gusiberiApi.util.updateQueryData("fetchDateRequest", {request_id}, dateRequest => {
            dateRequest.payload.date_participant_requests = dateRequest.payload.date_participant_requests?.map(p => p.id === prequest_id ? {...p, approval_status} : p)
          })
        )
        
        queryFulfilled.then(() => {
          getFeedback().showToast("status updated!", "success")
        }).catch(e => {
          getFeedback().showToast(retrieveErrorMessage(e), "error")
          patchResult.undo()
        })
      },
      invalidatesTags: ["fetch_date_request"]
    }),
    deleteParticipantRequest: builder.mutation<{message: string}, {request_id: string, prequest_id: number}>({
      query: body => ({ url: `date_requests/${body.request_id}/date_participant_requests/${body.prequest_id}`, method: 'DELETE' })
    }),
    fetchOpenDateRequests: builder.query<{payload: OpenDateRequest[]}, {}>({
      query: () => 'open_date_requests',
      providesTags: ['my_open_date_requests']
    }),
    deleteOpenDateRequest: builder.mutation<{message: string}, {slug: string}>({
      query: body => ({ url: `open_date_requests/${body.slug}`, method: 'DELETE' }),
      onQueryStarted: async ({slug}, { dispatch, queryFulfilled }) => {
        queryFulfilled.then(() => {
          dispatch(
            gusiberiApi.util.updateQueryData("fetchOpenDateRequests", {}, requests => {
              requests.payload = requests.payload.filter(r => r.slug !== slug)
            })
          )
        })
      },
      invalidatesTags: ['fetch_friends_open_date_request']
    }),
    updateOpenDateRequest: builder.mutation<{payload: OpenDateRequest}, {slug?: string} & OpenDateRequestsFormValues>({
      query: body => ({ url: `open_date_requests/${body.slug}`, method: 'PUT', body }),
      onQueryStarted: async ({slug, ...patch}, { dispatch, queryFulfilled }) => {
        const patchResult = dispatch(
          gusiberiApi.util.updateQueryData("fetchOpenDateRequests", {}, requests => {
            requests.payload = requests.payload.map(r => r.slug === slug ? {...r, ...patch} : r)
          })
        )

        queryFulfilled.catch(e => {
          patchResult.undo()
        })
      }
    }),
    addSuppliesSuggestion: builder.mutation<{payload: Required<SupplySuggestion>}, {request_id: string, payload: SupplySuggestion}>({
      query: body => ({ url: `date_requests/${body.request_id}/supplies_suggestions`, method: 'POST', body: body.payload }),
      onQueryStarted: async ({request_id, payload}, { dispatch, queryFulfilled }) => {
        const tempId = generateRandomNumber(5)
        const patchResult = dispatch(
          gusiberiApi.util.updateQueryData("fetchDateRequest", {request_id}, request => {
            request.payload = {...request.payload, supplies_suggestions: [...(request.payload.supplies_suggestions || []), {...payload, id: tempId}]}
          })
        )

        queryFulfilled.then(response => {
          dispatch(
            gusiberiApi.util.updateQueryData("fetchDateRequest", {request_id}, request => {
              const suggestions = (request.payload.supplies_suggestions || []).map(s => s.id === tempId ? response.data.payload : s)
              request.payload = {...request.payload, supplies_suggestions: suggestions}
            })
          )
        }).catch(e => {
          patchResult.undo()
        })
      }
    }),
    removeSuppliesSuggestion: builder.mutation<{message: string}, {request_id: string, id: number}>({
      query: body => ({ url: `date_requests/${body.request_id}/supplies_suggestions/${body.id}`, method: 'DELETE' }),
      onQueryStarted: async ({request_id, id}, { dispatch, queryFulfilled }) => {
        const patchResult = dispatch(
          gusiberiApi.util.updateQueryData("fetchDateRequest", {request_id}, request => {
            request.payload = {...request.payload, supplies_suggestions: request.payload.supplies_suggestions?.filter(s => s.id !== id)}
          })
        )

        queryFulfilled.catch(e => {
          patchResult.undo()
        })
      }
    }),
    pickSuppliesSuggestion: builder.mutation<{payload: Required<Supply>}, {request_id: string, id: number, suggestion?: SupplySuggestion, participantId?: number}>({
      query: body => ({ url: `date_requests/${body.request_id}/supplies_suggestions/${body.id}/select`, method: 'POST' }),
      onQueryStarted: async ({request_id, id, suggestion, participantId}, { dispatch, queryFulfilled }) => {
        if (!suggestion || !participantId) return
        const tempId = generateRandomNumber()
        const patchResult = dispatch(
          gusiberiApi.util.updateQueryData("fetchDateRequest", {request_id}, request => {
            request.payload = {
              ...request.payload, 
              supplies: [...(request.payload.supplies || []), {
                item: suggestion.item, 
                additional_info: suggestion.additional_info, 
                id: tempId,
                date_participant_id: participantId
              }]}
          })
        )

        queryFulfilled.then(result => {
          dispatch(
            gusiberiApi.util.updateQueryData("fetchDateRequest", {request_id}, request => {
              request.payload = {
                ...request.payload,
                supplies: request.payload.supplies?.map(s => s.id === tempId ? result.data.payload : s)
              }
            })
          )
        }).catch(e => {
          patchResult.undo()
        })
      }
    }),
    createOpenDateRequest: builder.mutation<{payload: OpenDateRequest}, OpenDateRequestsFormValues>({
      query: body => ({ url: 'open_date_requests', method: 'POST', body }),
      invalidatesTags: ['fetch_friends_open_date_request', 'my_open_date_requests'],
    }),
    acceptOpenRequestInterest: builder.mutation<{message: string}, {slug: string, id: number}>({
      query: body => ({ 
        url: `open_date_requests/${body.slug}/open_date_request_interests/${body.id}/accept`, 
        method: 'POST' 
      }),
      onQueryStarted: async ({slug, id}, {dispatch, queryFulfilled}) => {
        const patchResult = dispatch(
          gusiberiApi.util.updateQueryData("fetchOpenDateRequest", {slug}, request => {
            request.payload = {...request.payload, open_date_request_interests: request.payload.open_date_request_interests?.map(i => i.id === id ? {...i, status: "accepted"} : i)}
          })
        )
        queryFulfilled.catch(e => {
          patchResult.undo()
        })
      },
      invalidatesTags: ['my_open_date_requests'],
    }),
    rejectOpenRequestInterest: builder.mutation<{message: string}, {slug: string, id: number}>({
      query: body => ({
        url: `open_date_requests/${body.slug}/open_date_request_interests/${body.id}/reject`,
        method: 'POST'
      }),
      onQueryStarted: async ({slug, id}, {dispatch, queryFulfilled}) => {
        const patchResult = dispatch(
          gusiberiApi.util.updateQueryData("fetchOpenDateRequest", {slug}, request => {
            request.payload = {...request.payload, open_date_request_interests: request.payload.open_date_request_interests?.map(i => i.id === id ? {...i, status: "rejected"} : i)}
          })
        )
        queryFulfilled.catch(e => {
          patchResult.undo()
        })
      },
      invalidatesTags: ['my_open_date_requests'],
    }),
    createOpenRequestInterest: builder.mutation<{message: string}, {slug: string}>({
      query: body => ({
        url: `open_date_requests/${body.slug}/open_date_request_interests`,
        method: 'POST'
      }),
      onQueryStarted: async ({slug}, { dispatch, queryFulfilled }) => {
        const patchResult = dispatch(
          gusiberiApi.util.updateQueryData("fetchFriendsOpenDateRequest", {}, request => {
            const currentUser = selectCurrentUser(getSharedStore().getState())
            request.payload = request.payload.map(r => {
              if (r.slug !== slug) return r
              if (r.open_date_request_interests?.some(i => i.user.username === currentUser?.username)) {
                return {...r, open_date_request_interests: r.open_date_request_interests?.filter(i => i.user.username !== currentUser?.username)}
              }
              return {...r, open_date_request_interests: [...(r.open_date_request_interests || []), {user: currentUser, status: "pending", id: generateRandomNumber()}]}
            })
          })
        )
        const timelinePatch = dispatch(
          gusiberiApi.util.updateQueryData("fetchTimeline", undefined, timeline => {
            const currentUser = selectCurrentUser(getSharedStore().getState())
            timeline.payload.forEach(activity => {
              if (activity.group !== 'open_date_request' || activity.object?.slug !== slug) return
              const invite = activity.object
              if (invite.viewer_interested) {
                invite.viewer_interested = false
                invite.interests_count = Math.max(0, invite.interests_count - 1)
                invite.interested_users = invite.interested_users.filter(u => u.username !== currentUser?.username)
              } else {
                invite.viewer_interested = true
                invite.interests_count += 1
                if (currentUser && invite.interested_users.length < 4) {
                  invite.interested_users.push({
                    username: currentUser.username,
                    full_name: currentUser.full_name,
                    avatar: currentUser.avatar ?? null,
                  })
                }
              }
            })
          })
        )
        const requestPatch = dispatch(
          gusiberiApi.util.updateQueryData("fetchOpenDateRequest", {slug}, request => {
            const currentUser = selectCurrentUser(getSharedStore().getState())
            if (!currentUser) return
            const interests = request.payload.open_date_request_interests || []
            const alreadyInterested = interests.some(i => i.user.username === currentUser.username)
            request.payload.open_date_request_interests = alreadyInterested
              ? interests.filter(i => i.user.username !== currentUser.username)
              : [...interests, {user: currentUser, status: "pending", id: generateRandomNumber()}]
          })
        )
        queryFulfilled.catch(e => {
          patchResult.undo()
          timelinePatch.undo()
          requestPatch.undo()
        })
      }
    }),
    closeOpenDateRequest: builder.mutation<{payload: DateRequest}, {slug: string}>({
      query: body => ({ url: `open_date_requests/${body.slug}/close_request`, method: 'POST' }),
      invalidatesTags: ['fetch_friends_open_date_request', 'my_open_date_requests'],
      onQueryStarted: async ({slug}, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled
        } catch {
          return
        }
        dispatch(
          gusiberiApi.util.updateQueryData("fetchOpenDateRequest", {slug}, request => {
            request.payload.status = "closed"
          })
        )
        dispatch(
          gusiberiApi.util.updateQueryData("fetchTimeline", undefined, timeline => {
            timeline.payload.forEach(activity => {
              if (activity.group === 'open_date_request' && activity.object?.slug === slug) {
                activity.object.status = 'closed'
              }
            })
          })
        )
      }
    }),
    fetchOpenDateRequest: builder.query<{payload: OpenDateRequest}, {slug: string}>({
      query: body => `open_date_requests/${body.slug}`
    }),
    fetchFriendsOpenDateRequest: builder.query<{payload: OpenDateRequest[]}, {}>({
      query: () => 'open_date_requests/timeline',
      providesTags: ['fetch_friends_open_date_request']
    }),
    markOpenRequestAsSeen: builder.mutation<{}, {slug: string}>({
      query: body => ({ url: `open_date_requests/${body.slug}/viewed`, method: 'POST' })
    }),
    updateLastOnline: builder.mutation<{}, {}>({
      query: _ => ({ url: `users/update_activity?activity_type=last_online`, method: 'POST' })
    }),
    setDemoViewed: builder.mutation<{}, {}>({
      query: _ => ({ url: `users/update_activity?activity_type=demo_viewed`, method: 'POST' })
    }),
    createBuckBreaker: builder.mutation<{payload: TBuckBreaker}, {title: string, description: string}>({
      query: body => ({ url: 'buckbreakers', method: 'POST', body })
    }),
    fetchBuckBreakers: builder.query<FetchBuckBreakersResponse, FetchBuckBreakersRequest | void>({
      query: (arg) => {
        const page =
          arg && typeof arg === 'object' && arg.page != null ? arg.page : 1
        return `buckbreakers?page=${page}`
      },
      serializeQueryArgs: ({ endpointName }) => endpointName,
      merge: (currentCache, incoming, { arg }) => {
        const page =
          arg && typeof arg === 'object' && arg.page != null ? arg.page : 1
        if (page <= 1 || !currentCache.payload?.length) {
          currentCache.payload = incoming.payload
          currentCache.pagination_info = incoming.pagination_info
          return
        }
        const existingIds = new Set(currentCache.payload.map((b) => b.friendly_id))
        currentCache.payload.push(
          ...incoming.payload.filter((b) => !existingIds.has(b.friendly_id))
        )
        currentCache.pagination_info = incoming.pagination_info
      },
      forceRefetch: ({ currentArg, previousArg }) => {
        const currentPage =
          currentArg && typeof currentArg === 'object' && currentArg.page != null
            ? currentArg.page
            : 1
        const previousPage =
          previousArg && typeof previousArg === 'object' && previousArg.page != null
            ? previousArg.page
            : 1
        return currentPage !== previousPage
      },
    }),
    openScoreboards: builder.query<FetchScoreboardsResponse, FetchScoreboardsRequest | void>({
      query: (arg) => {
        const page =
          arg && typeof arg === 'object' && arg.page != null ? arg.page : 1
        return `scoreboards?page=${page}`
      },
      serializeQueryArgs: ({ endpointName }) => endpointName,
      merge: (currentCache, incoming, { arg }) => {
        const page =
          arg && typeof arg === 'object' && arg.page != null ? arg.page : 1
        if (page <= 1 || !currentCache.payload?.length) {
          currentCache.payload = incoming.payload
          currentCache.pagination_info = incoming.pagination_info
          return
        }
        const existingIds = new Set(currentCache.payload.map((s) => s.id))
        currentCache.payload.push(
          ...incoming.payload.filter((s) => !existingIds.has(s.id))
        )
        currentCache.pagination_info = incoming.pagination_info
      },
      forceRefetch: ({ currentArg, previousArg }) => {
        const currentPage =
          currentArg && typeof currentArg === 'object' && currentArg.page != null
            ? currentArg.page
            : 1
        const previousPage =
          previousArg && typeof previousArg === 'object' && previousArg.page != null
            ? previousArg.page
            : 1
        return currentPage !== previousPage
      },
    }),
    createOpenScoreboard: builder.mutation<CreateOpenScoreboardResponse, CreateOpenScoreboardRequest>({
      query: body => ({ url: 'scoreboards', method: 'POST', body }),
      onQueryStarted: async (body, { dispatch, queryFulfilled }) => {
        getFeedback().setLoadingMessage("Creating scoreboard...")
        try {
          const response = await queryFulfilled
          dispatch(
            gusiberiApi.util.updateQueryData("openScoreboards", { page: 1 }, scoreboards => {
              scoreboards.payload = [response.data.payload, ...scoreboards.payload]
            })
          )
        } catch (e) {
          getFeedback().showToast(retrieveErrorMessage(e), "error")
        }
        getFeedback().clearLoading()
      }
    }),
    fetchScoreboardResults: builder.query<FetchScoreboardResultsResponse, {id: number, page?: number}>({
      query: body => `scoreboards/${body.id}/scoreboard_results?page=${body.page || 1}`
    }),
    fetchScoreboardResult: builder.query<FetchScoreboardResultResponse, FetchScoreboardResultRequest>({
      query: body => `scoreboards/${body.scoreboard_id}/scoreboard_results/${body.result_id}`
    }),
    createScoreboardResult: builder.mutation<CreateScoreboardResultResponse, CreateScoreboardResultRequest>({
      query: body => ({ url: `scoreboards/${body.scoreboard_id}/scoreboard_results`, method: 'POST', body }),
      onQueryStarted: async ({scoreboard_id, ...body}, { dispatch, queryFulfilled }) => {
        queryFulfilled.then(result => {
          dispatch(
            gusiberiApi.util.updateQueryData("fetchScoreboardResults", {id: scoreboard_id, page: body.page}, scoreboard => {
              scoreboard.payload = [result.data.payload, ...scoreboard.payload]
            })
          )
        }).catch(e => {
          getFeedback().showToast(retrieveErrorMessage(e), "error")
        })
      }
    }),
    updateScoreboardResult: builder.mutation<UpdateScoreboardResultResponse, UpdateScoreboardResultRequest>({
      query: body => ({ url: `scoreboards/${body.scoreboard_id}/scoreboard_results/${body.result_id}`, method: 'PUT', body }),
      onQueryStarted: async ({scoreboard_id, result_id, ...body}, { dispatch, queryFulfilled }) => {
        queryFulfilled.then(updatedResult => {
          dispatch(
            gusiberiApi.util.updateQueryData("fetchScoreboardResults", {id: scoreboard_id, page: body.page}, scoreboard => {
              scoreboard.payload = scoreboard.payload.map(result => result.id === result_id ? updatedResult.data.payload : result)
            })
          )
          getFeedback().closeDialog()
        }).catch(e => {
          getFeedback().showToast(retrieveErrorMessage(e), "error")
        })
      }
    }),
    deleteScoreboardResult: builder.mutation<{message: string}, {scoreboard_id: number, result_id: number}>({
      query: body => ({ url: `scoreboards/${body.scoreboard_id}/scoreboard_results/${body.result_id}`, method: 'DELETE' }),
      onQueryStarted: async ({scoreboard_id, result_id}, { dispatch, queryFulfilled }) => {
        const patchResult = dispatch(
          gusiberiApi.util.updateQueryData("fetchScoreboardResults", {id: scoreboard_id}, scoreboard => {
            scoreboard.payload = scoreboard.payload.filter(result => result.id !== result_id)
          })
        )
        queryFulfilled.catch(e => {
          patchResult.undo()
          getFeedback().showToast(retrieveErrorMessage(e), "error")
        })
      }
    }),
    fetchScoreboardDetail: builder.query<FetchScoreboardDetailResponse, FetchScoreboardDetailRequest>({
      query: body => `scoreboards/${body.id}`
    }),
    fetchScoreboardTable: builder.query<FetchScoreboardTableResponse, FetchScoreboardTableRequest>({
      query: body => `scoreboards/${body.id}/scoreboard_table`
    }),
    deleteScoreboard: builder.mutation<{message: string}, {id: number}>({
      query: body => ({ url: `scoreboards/${body.id}`, method: 'DELETE' }),
    }),
    addScoreboardParticipant: builder.mutation<AddScoreboardParticipantResponse, {id: number} & AddScoreboardParticipantRequest>({
      query: body => ({url: `scoreboards/${body.id}/participants`, method: 'POST', body}),
      onQueryStarted: async ({id, ...patch}, { dispatch, queryFulfilled }) => {
        queryFulfilled.then((result) => {
          dispatch(
            gusiberiApi.util.updateQueryData("fetchScoreboardDetail", {id}, scoreboard => {
              scoreboard.payload.scoreboard_participants = [...scoreboard.payload.scoreboard_participants, result.data.payload]
            })
          )
        }).catch(e => {
          getFeedback().showToast(retrieveErrorMessage(e), "error")
        })
      }
    }),
    removeScoreboardParticipant: builder.mutation<{message: string}, {id: number, participant_id: number}>({
      query: body => ({ url: `scoreboards/${body.id}/participants/${body.participant_id}`, method: 'DELETE' }),
      onQueryStarted: async ({ id, participant_id }, { dispatch, queryFulfilled }) => {
        const patchResult = dispatch(
          gusiberiApi.util.updateQueryData("fetchScoreboardDetail", { id }, scoreboard => {
            scoreboard.payload.scoreboard_participants = (scoreboard.payload.scoreboard_participants || []).filter(
              p => p.id !== participant_id
            )
          })
        )

        try {
          await queryFulfilled
        } catch (e) {
          patchResult.undo()
          getFeedback().showToast(retrieveErrorMessage(e), 'error')
        }
      }
    }),
    fetchBuckBreaker: builder.query<{payload: TBuckBreaker}, {id: string}>({
      query: body => `buckbreakers/${body.id}`
    }),
    updateBuckBreaker: builder.mutation<{payload: TBuckBreaker}, {id: string, title: string, description: string}>({
      query: body => ({ url: `buckbreakers/${body.id}`, method: 'PUT', body }),
      onQueryStarted: async ({id, ...patch}, { dispatch, queryFulfilled }) => {
        queryFulfilled.then((result) => {
          dispatch(
            gusiberiApi.util.updateQueryData("fetchBuckBreaker", {id}, buckBreakers => {
              buckBreakers.payload = result.data.payload
            })
          )
        })
      }
    }),
    addBBParticipant: builder.mutation<{payload: TBuckBreaker}, {id: string, name: string, contact: string}>({
      query: body => ({ url: `buckbreakers/${body.id}/participants`, method: 'POST', body }),
      onQueryStarted: async ({id, ...patch}, { dispatch, queryFulfilled }) => {
        queryFulfilled.then((result) => {
          dispatch(
            gusiberiApi.util.updateQueryData("fetchBuckBreaker", {id}, buckBreakers => {
              buckBreakers.payload = result.data.payload
            })
          )
        })
      }
    }),
    removeBBParticipant: builder.mutation<{payload: TBuckBreaker}, {id: string, pid: number}>({
      query: body => ({ url: `buckbreakers/${body.id}/participants/${body.pid}`, method: 'DELETE' }),
      onQueryStarted: async ({id, ...patch}, { dispatch, queryFulfilled }) => {
        queryFulfilled.then((result) => {
          dispatch(
            gusiberiApi.util.updateQueryData("fetchBuckBreaker", {id}, buckBreakers => {
              buckBreakers.payload = result.data.payload
            })
          )
        })
      }
    }),
    updateBBParticipant: builder.mutation<{payload: TBuckBreaker}, {id: string, pid: number, name: string, contact: string}>({
      query: body => ({ url: `buckbreakers/${body.id}/participants/${body.pid}`, method: 'PUT', body })
    }),
    addBBExpense: builder.mutation<{payload: TBuckBreaker}, TBuckBreakerExpense>({
      query: body => ({ url: `buckbreakers/${body.id}/expenses`, method: 'POST', body }),
      onQueryStarted: async ({id, ...patch}, { dispatch, queryFulfilled }) => {
        queryFulfilled.then((result) => {
          dispatch(
            gusiberiApi.util.updateQueryData("fetchBuckBreaker", {id}, buckBreakers => {
              buckBreakers.payload = result.data.payload
            })
          )
        })
      }
    }),
    updateBBExpense: builder.mutation<{payload: TBuckBreaker}, TBuckBreakerExpense & {expense_id: string}>({
      query: body => ({ url: `buckbreakers/${body.id}/expenses/${body.expense_id}`, method: 'PUT', body }),
      onQueryStarted: async ({id, ...patch}, { dispatch, queryFulfilled }) => {
        queryFulfilled.then((result) => {
          dispatch(
            gusiberiApi.util.updateQueryData("fetchBuckBreaker", {id}, buckBreakers => {
              buckBreakers.payload = result.data.payload
            })
          )
        })
      }
    }),
    deleteBBExpense: builder.mutation<{payload: TBuckBreaker}, {id: string, expense_id: string}>({
      query: body => ({ url: `buckbreakers/${body.id}/expenses/${body.expense_id}`, method: 'DELETE' }),
      onQueryStarted: async ({id, ...patch}, { dispatch, queryFulfilled }) => {
        queryFulfilled.then((result) => {
          dispatch(
            gusiberiApi.util.updateQueryData("fetchBuckBreaker", {id}, buckBreakers => {
              buckBreakers.payload = result.data.payload
            })
          )
        })
      }
    }),
    markBBExpenseAsPaid: builder.mutation<{payload: TBuckBreaker}, {id: string, participant_id: string, proof?: string}>({
      query: body => ({ url: `buckbreakers/${body.id}/mark_as_paid`, method: 'POST', body }),
      onQueryStarted: async ({id, ...patch}, { dispatch, queryFulfilled }) => {
        queryFulfilled.then((result) => {
          dispatch(
            gusiberiApi.util.updateQueryData("fetchBuckBreaker", {id}, buckBreakers => {
              buckBreakers.payload = result.data.payload
            })
          )
        })
      }
    })
  }),
})

export const {
  useLoginMutation,
  useRegisterMutation,
  useLazyCurrentUserQuery,
  useCurrentUserQuery,
  useDateScheduleQuery,
  usePreferredDatingActivityQuery,
  useLazyDateScheduleQuery,
  useLazyPreferredDatingActivityQuery,
  useFetchUpcomingDatesQuery,
  useLazyFetchUpcomingDatesQuery,
  useDeactivateDayMutation,
  useActivateDayMutation,
  useAddScheduleMutation,
  useDeleteScheduleMutation,
  useSetUserProfileInfoMutation,
  useChangePasswordMutation,
  useDeleteAccountMutation,
  useSendSupportMessageMutation,
  useFetchSupportIssuesQuery,
  usePostSupportConversationMutation,
  useFetchSupportConversationQuery,
  useFetchFavouriteActivitiesQuery,
  usePostFavouriteActivitiesMutation,
  useDeleteFavouriteActivityMutation,
  useUpdateFavouriteActivitiesMutation,
  useFetchInterestsQuery,
  useLazySearchUsersQuery,
  useProfileQuery,
  useAvailabilityQuery,
  useBusyTimesQuery,
  useSendDateRequestMutation,
  useUpdateSentDateRequestMutation,
  useAcceptDateRequestMutation,
  useDeclineDateRequestMutation,
  useLazyFetchDateRequestQuery,
  useFetchActivePendingRequestQuery,
  useFetchSentRequestsQuery,
  useFetchReceivedRequestsQuery,
  useRescheduleDateRequestMutation,
  useFetchPastDatesQuery,
  useAddReviewRequestMutation,
  useToggleRecurringMutation,
  useSendFriendRequestMutation,
  useCreateFriendGroupRequestMutation,
  useFetchFriendGroupsRequestQuery,
  useFetchPendingFriendRequestsQuery,
  useFetchDeclinedFriendRequestsQuery,
  useAcceptFriendRequestMutation,
  useGetFriendsRequestQuery,
  useRemoveFriendRequestMutation,
  useDeclineFriendRequestMutation,
  useRenameFriendGroupRequestMutation,
  useAssignFriendsToGroupRequestMutation,
  useRemoveFriendsFromGroupRequestMutation,
  useDeleteFriendGroupRequestMutation,
  useFetchNotificationsQuery,
  useFetchTimelineQuery,
  useLazyFetchTimelineQuery,
  useMarkNotificationAsSeenMutation,
  useSendChatMessageMutation,
  useLazyFetchChatMessagesQuery,
  useFetchChatMessagesQuery,
  useFetchDateRequestQuery,
  useMarkMessagesAsSeenMutation,
  useResendActivationMutation,
  useActivateUserMutation,
  useResetPasswordRequestMutation,
  useResetPasswordMutation,
  useGoogleAuthMutation,
  useAddParticipantMutation,
  useRemoveParticipantMutation,
  useFetchDateScoreboardRequestQuery,
  useFetchScoreboardQuery,
  useSaveDateScoreboardRequestMutation,
  useFetchDateCommentsQuery,
  useSaveDateCommentsMutation,
  useDeleteMemoryRequestMutation,
  useFetchDateMediaQuery,
  useSaveDateMediaMutation,
  useSaveDateExpenseMutation,
  useUpdateDateExpenseMutation,
  useDeleteDateExpenseMutation,
  useFetchDateExpenseQuery,
  useFetchPastPendingRequestQuery,
  useResendParticipantInviteMutation,
  useScheduleAnotherMutation,
  useSetNotificationSettingsMutation,
  useDeleteDateRequestMutation,
  useSendParticipantRequestMutation,
  useDeleteParticipantRequestMutation,
  useUpdateParticipantRequestMutation,
  useLazyFetchDateScoreboardRequestQuery,
  useLazyFetchScoreboardQuery,
  useFetchOpenDateRequestsQuery,
  useDeleteOpenDateRequestMutation,
  useUpdateOpenDateRequestMutation,
  useCreateOpenDateRequestMutation,
  useAcceptOpenRequestInterestMutation,
  useRejectOpenRequestInterestMutation,
  useCreateOpenRequestInterestMutation,
  useCloseOpenDateRequestMutation,
  useLazyFetchOpenDateRequestQuery,
  useFetchOpenDateRequestQuery,
  useFetchFriendsOpenDateRequestQuery,
  useMarkOpenRequestAsSeenMutation,
  useMarkAllNotificationAsSeenMutation,
  useSetDemoViewedMutation,
  useUpdateLastOnlineMutation,
  useAddSuppliesSuggestionMutation,
  useRemoveSuppliesSuggestionMutation,
  usePickSuppliesSuggestionMutation,
  useMarkExpenseAsPaidMutation,
  useFetchBuckBreakerQuery,
  useFetchBuckBreakersQuery,
  useOpenScoreboardsQuery,
  useCreateOpenScoreboardMutation,
  useFetchScoreboardResultsQuery,
  useFetchScoreboardResultQuery,
  useCreateScoreboardResultMutation,
  useUpdateScoreboardResultMutation,
  useDeleteScoreboardResultMutation,
  useFetchScoreboardDetailQuery,
  useFetchScoreboardTableQuery,
  useLazyFetchScoreboardTableQuery,
  useDeleteScoreboardMutation,
  useAddScoreboardParticipantMutation,
  useRemoveScoreboardParticipantMutation,
  useCreateBuckBreakerMutation,
  useUpdateBuckBreakerMutation,
  useAddBBParticipantMutation,
  useRemoveBBParticipantMutation,
  useUpdateBBParticipantMutation,
  useDeleteBBExpenseMutation,
  useAddBBExpenseMutation,
  useUpdateBBExpenseMutation,
  useMarkBBExpenseAsPaidMutation,
  useUpsertPushTokenMutation,
  useDeletePushTokenMutation,
  useFetchRealtimeConfigQuery
} = gusiberiApi

export default gusiberiApi
